import React from 'react';
import * as S from './styles';
import { UseFormRegisterReturn } from 'react-hook-form';
import useText from 'app/presentation/hooks/useText';

interface Validation {
    validate: (value: string) => boolean;
    message: string;
}

interface Props extends React.InputHTMLAttributes<HTMLInputElement> {
    registerValues?: UseFormRegisterReturn;
    customValidation?: Validation;
}

export const Input: React.FC<Props> = ({
    registerValues,
    customValidation,
    required,
    ...props
}) => {
    const { textGetter } = useText();
    const t = textGetter('agent.components.input');

    const {
        onChange: registerOnChange = undefined,
        ref: registerRef,
        ...registerRest
    } = registerValues ? registerValues : { ref: undefined };

    const ref = React.useRef<HTMLInputElement>(null);
    React.useEffect(() => {
        const el = ref.current;
        if (!el || !registerRef) {
            return;
        }

        registerRef(el);
    }, [registerRef, ref.current]);

    const validateValue = () => {
        const el = ref.current;
        if (!el) {
            return;
        }

        if (required && !el.value) {
            el.setCustomValidity(t('required'));
        } else if (customValidation && !customValidation.validate(el.value)) {
            el.setCustomValidity(customValidation.message);
        } else {
            el.setCustomValidity('');
        }
    };

    React.useEffect(() => {
        validateValue();
    }, [ref.current, required, customValidation, props.value]);

    const onChange = (event: React.ChangeEvent<HTMLInputElement>): void => {
        if (registerOnChange) {
            registerOnChange(event);
        }

        if (props.onChange) {
            props.onChange(event);
        }

        validateValue();
    };

    return (
        <S.Input {...registerRest} {...props} ref={ref} onChange={onChange} />
    );
};
